// Week / day grid: day headers, an all-day lane with spanning bars, and a scrollable
// hourly grid where tasks with a due time sit at their hour. Shared by the Calendar view and Planner.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useDroppable } from '@dnd-kit/core';
import type { Task } from '@shared/types';
import { Popover } from '@/components/ui/Popover';
import { useCalDnd, type CalDropData } from './CalendarDnd';
import { DayTasksPopover, QuickAdd, TaskChip, TimedBlock } from './TaskChip';
import { HOUR_H, LANE_H, dayOf, formatClock, hourLabel, isTimedTask, layoutSegments, layoutTimed, limitSegments, minutesOfDay, type AddTarget } from './calendarUtils';

/** the all-day lane may take up to this share of the grid height before "+N more" kicks in */
const ALLDAY_MAX_SHARE = 0.35;
const FIRST_VISIBLE_HOUR = 6;

export interface TimeGridProps {
  days: number[];
  tasks: Task[];
  today: number;
  adding?: AddTarget | null;
  onAdd?: (t: AddTarget | null) => void;
  onCreate?: (name: string, t: AddTarget) => void;
  onOpenDay?: (day: number) => void;
}

/** Current time, refreshed every minute. */
export function useNow(intervalMs = 60_000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function TimeGrid({ days, tasks, today, adding, onAdd, onCreate, onOpenDay }: TimeGridProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);
  const n = days.length;
  const cols = `56px repeat(${n}, minmax(0, 1fr))`;

  const { allDay, timedByDay } = useMemo(() => {
    const allDay: Task[] = [];
    const timedByDay = new Map<number, Task[]>();
    for (const t of tasks) {
      if (isTimedTask(t)) {
        const d = dayOf(t.dueDate!);
        const arr = timedByDay.get(d) ?? [];
        arr.push(t);
        timedByDay.set(d, arr);
      } else allDay.push(t);
    }
    return { allDay, timedByDay };
  }, [tasks]);

  const [gridH, setGridH] = useState(800);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setGridH(el.clientHeight));
    ro.observe(el);
    setGridH(el.clientHeight);
    return () => ro.disconnect();
  }, []);
  const maxLanes = Math.max(3, Math.floor((gridH * ALLDAY_MAX_SHARE) / LANE_H));
  const segs = useMemo(() => layoutSegments(allDay, days[0], n, 'ad'), [allDay, days, n]);
  const lim = useMemo(() => limitSegments(segs, n, maxLanes), [segs, n, maxLanes]);
  const addingAllDay = adding && adding.minutes === null && days.includes(adding.day) ? adding.day : null;
  const laneRows = Math.max(1, lim.lanesUsed + (addingAllDay !== null ? 1 : 0));

  // start the day at 6 AM
  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = FIRST_VISIBLE_HOUR * HOUR_H - 10;
  }, []);

  /** visible part of the hour columns (below the sticky header, inside the scroller) */
  const inside = useMemo(
    () => (x: number, y: number) => {
      const sc = scrollRef.current?.getBoundingClientRect();
      const hd = headRef.current?.getBoundingClientRect();
      if (!sc) return false;
      return x >= sc.left && x <= sc.right && y >= (hd ? hd.bottom : sc.top) && y <= sc.bottom;
    },
    [],
  );

  const now = useNow();

  return (
    <div className={clsx('cal-tg', { single: n === 1 })} ref={scrollRef}>
      <div className="cal-tg-head" ref={headRef}>
        <div className="cal-tg-dows" style={{ gridTemplateColumns: cols }}>
          <div className="cal-tg-gutter-head">{format(days[0], 'O')}</div>
          {days.map((d) => {
            const dow = new Date(d).getDay();
            return (
              <button
                key={d}
                className={clsx('cal-tg-dow', { today: d === today, weekend: dow === 0 || dow === 6, clickable: !!onOpenDay && n > 1 })}
                onClick={() => n > 1 && onOpenDay?.(d)}
              >
                <span className="cal-tg-dow-name">{format(d, n === 1 ? 'EEEE' : 'EEE')}</span>
                <span className="cal-tg-dow-num">{format(d, 'd')}</span>
              </button>
            );
          })}
        </div>
        <div className="cal-tg-allday" style={{ gridTemplateColumns: cols }}>
          <div className="cal-tg-allday-label">All day</div>
          <div className="cal-tg-allday-days" style={{ gridColumn: `2 / span ${n}`, gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`, height: laneRows * LANE_H + 8 }}>
            {days.map((d) => (
              <AllDayCell key={d} day={d} today={today} onAdd={onAdd} />
            ))}
            <div className="cal-week-events">
              {lim.visible.map((s) => (
                <TaskChip
                  key={s.key}
                  seg={s}
                  idPrefix="ad"
                  style={{
                    top: 4 + s.lane * LANE_H,
                    left: `calc(${(s.startCol / n) * 100}% + ${s.contLeft ? 0 : 4}px)`,
                    width: `calc(${((s.endCol - s.startCol + 1) / n) * 100}% - ${(s.contLeft ? 0 : 4) + (s.contRight ? 0 : 4)}px)`,
                  }}
                />
              ))}
              {lim.more.map((cnt, c) =>
                cnt > 0 ? (
                  <Popover key={c} placement="bottom-start" content={(close) => <DayTasksPopover day={days[c]} tasks={lim.perDay[c].map((s) => s.task)} close={close} />}>
                    <button className="cal-more" style={{ top: 4 + lim.moreLane[c] * LANE_H, left: `calc(${(c / n) * 100}% + 4px)`, width: `calc(${100 / n}% - 8px)` }}>
                      +{cnt} more
                    </button>
                  </Popover>
                ) : null,
              )}
              {addingAllDay !== null && onCreate && onAdd && (
                <QuickAdd
                  className="in-lane"
                  style={{
                    top: 4 + lim.lanesUsed * LANE_H,
                    left: `calc(${(days.indexOf(addingAllDay) / n) * 100}% + 4px)`,
                    width: `max(160px, calc(${100 / n}% - 8px))`,
                  }}
                  onSubmit={(name) => onCreate(name, { day: addingAllDay, minutes: null })}
                  onCancel={() => onAdd(null)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="cal-tg-body" style={{ gridTemplateColumns: cols, height: 24 * HOUR_H }}>
        <div className="cal-tg-gutter">
          {Array.from({ length: 24 }, (_, h) => (
            <div key={h} className="cal-tg-hour" style={{ top: h * HOUR_H }}>
              {h > 0 ? hourLabel(h) : ''}
            </div>
          ))}
        </div>
        {days.map((d) => (
          <TimeColumn
            key={d}
            day={d}
            tasks={timedByDay.get(d) ?? []}
            isToday={d === today}
            now={now}
            inside={inside}
            adding={adding && adding.day === d && adding.minutes !== null ? adding.minutes : null}
            onAdd={onAdd}
            onCreate={onCreate}
            compact={n > 1}
          />
        ))}
      </div>
    </div>
  );
}

function AllDayCell({ day, today, onAdd }: { day: number; today: number; onAdd?: (t: AddTarget | null) => void }) {
  const data: CalDropData = { kind: 'day', day, allDay: true };
  const { setNodeRef, isOver } = useDroppable({ id: `aday:${day}`, data });
  const { suppressClick, dayRange } = useCalDnd();
  const inRange = !!dayRange && day >= dayRange.start && day <= dayRange.end;
  const dow = new Date(day).getDay();
  return (
    <div
      ref={setNodeRef}
      className={clsx('cal-tg-allday-cell', { over: isOver || inRange, today: day === today, weekend: dow === 0 || dow === 6, clickable: !!onAdd })}
      onClick={() => {
        if (!onAdd || suppressClick()) return;
        onAdd({ day, minutes: null });
      }}
    />
  );
}

function TimeColumn({
  day,
  tasks,
  isToday,
  now,
  inside,
  adding,
  onAdd,
  onCreate,
  compact,
}: {
  day: number;
  tasks: Task[];
  isToday: boolean;
  now: number;
  inside: (x: number, y: number) => boolean;
  adding: number | null;
  onAdd?: (t: AddTarget | null) => void;
  onCreate?: (name: string, t: AddTarget) => void;
  compact: boolean;
}) {
  const el = useRef<HTMLDivElement | null>(null);
  const data: CalDropData = { kind: 'time', day, el, inside };
  const { setNodeRef, isOver } = useDroppable({ id: `tcol:${day}`, data });
  const ref = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      el.current = node;
    },
    [setNodeRef],
  );
  const { preview, suppressClick } = useCalDnd();
  const items = useMemo(() => layoutTimed(tasks), [tasks]);
  const dow = new Date(day).getDay();
  const nowMin = minutesOfDay(now);
  return (
    <div
      ref={ref}
      className={clsx('cal-tg-col', { over: isOver, today: isToday, weekend: dow === 0 || dow === 6, clickable: !!onAdd })}
      style={{ backgroundSize: `100% ${HOUR_H}px` }}
      onClick={(e) => {
        if (!onAdd || suppressClick()) return;
        const r = e.currentTarget.getBoundingClientRect();
        const m = Math.floor((((e.clientY - r.top) / HOUR_H) * 60) / 30) * 30;
        onAdd({ day, minutes: Math.max(0, Math.min(23 * 60 + 30, m)) });
      }}
    >
      {items.map((it) => (
        <TimedBlock
          key={it.task.id}
          task={it.task}
          compact={it.endMin - it.startMin <= 30 || (compact && it.cols > 2)}
          style={{
            top: (it.startMin / 60) * HOUR_H + 1,
            height: Math.max(22, ((it.endMin - it.startMin) / 60) * HOUR_H - 2),
            left: `calc(${(it.col / it.cols) * 100}% + 2px)`,
            width: `calc(${100 / it.cols}% - 4px)`,
          }}
        />
      ))}
      {preview && preview.day === day && (
        <div className="cal-tg-preview" style={{ top: (preview.minutes / 60) * HOUR_H, height: Math.max(22, (preview.duration / 60) * HOUR_H) }}>
          {formatClock(preview.minutes)}
        </div>
      )}
      {isToday && (
        <div className="cal-tg-now" style={{ top: (nowMin / 60) * HOUR_H }}>
          <span className="cal-tg-now-dot" />
        </div>
      )}
      {adding !== null && onCreate && onAdd && (
        <QuickAdd
          className="in-slot"
          label={formatClock(adding)}
          style={{ top: (adding / 60) * HOUR_H + 1 }}
          onSubmit={(name) => onCreate(name, { day, minutes: adding })}
          onCancel={() => onAdd(null)}
        />
      )}
    </div>
  );
}
